/**
 * `<MoveHereRung/>` — the ladder's rung "Move someone here" (#2412).
 *
 * Sits under rung 0 (free people) and above recall and cancel leave. It names
 * people seated on another ward that session whose ward stays complete
 * without them. Two groups when the team sets cover groups: Stretch (a ward in
 * the short ward's group) and Cover from another ward. A team with no groups
 * sees one list, "Can move here". The rung is lead-only, as the whole ladder
 * is. The row markup and the confirm step belong to the modal, which passes
 * `renderRow`; this component owns the grouping and the "Show all" cut.
 */
import type { ReactNode } from "react";

import type { ShortfallMoveCandidate } from "../../api/useShortfallOptions";

/** Rows shown before "Show all N": the rung must not bury the two below it. */
export const MOVE_SHOWN = 4;

const GROUPS: { key: ShortfallMoveCandidate["group"]; title: string | null }[] = [
  { key: "stretch", title: "Stretch" },
  { key: "cover", title: "Cover from another ward" },
  { key: "any", title: null },
];

/** The row's middle line: `leaves 64B (has 3, needs 2)`, then the day panel's
 *  stretched fact when there is one (`6 days running`). */
export function moveLine(candidate: ShortfallMoveCandidate): string {
  return candidate.stretched ? `${candidate.why} · ${candidate.stretched}` : candidate.why;
}

interface MoveHereRungProps {
  cellKey: string;
  candidates: ShortfallMoveCandidate[];
  expanded: boolean;
  onToggle: () => void;
  /** A parked question keeps its row on screen past the cut. */
  pendingId: string | null;
  renderRow: (candidate: ShortfallMoveCandidate) => ReactNode;
}

export function MoveHereRung({
  cellKey,
  candidates,
  expanded,
  onToggle,
  pendingId,
  renderRow,
}: MoveHereRungProps) {
  if (candidates.length === 0) return null;
  const shown = candidates.filter(
    (c, i) => expanded || i < MOVE_SHOWN || c.membership_id === pendingId,
  );
  const hidden = candidates.length - shown.length;
  return (
    <div className="space-y-1.5" data-testid={`shortfall-move-${cellKey}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-fg-faint">
        Move someone here · {candidates.length}
      </p>
      {GROUPS.map(({ key, title }) => {
        const rows = shown.filter((c) => c.group === key);
        if (rows.length === 0) return null;
        return (
          <div
            key={key}
            className="space-y-1.5"
            data-testid={`shortfall-move-group-${key}-${cellKey}`}
          >
            <p className="text-[11px] font-medium text-fg-muted">{title ?? "Can move here"}</p>
            {rows.map((c) => renderRow(c))}
          </div>
        );
      })}
      {candidates.length > MOVE_SHOWN ? (
        <p className="text-[11px] text-fg-subtle">
          {expanded ? `All ${candidates.length} shown` : `${hidden} more can move here`}
          {" · "}
          <button
            type="button"
            data-testid={`shortfall-move-toggle-${cellKey}`}
            className="inline-flex min-h-tap items-center underline underline-offset-2 sm:min-h-0"
            onClick={onToggle}
          >
            {expanded ? "Show fewer" : `Show all ${candidates.length}`}
          </button>
        </p>
      ) : null}
    </div>
  );
}
